import React, { useMemo } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

const GATE_COLORS = [
  "hsl(var(--primary))",         // cyan
  "hsl(var(--warning))",         // amber
  "hsl(var(--destructive))",     // red
  "hsl(217 91% 60%)",            // blue
];

const fmtTime = (iso) => {
  try {
    const d = new Date(iso);
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  } catch { return ""; }
};

const TooltipBox = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="panel-elevated p-2 text-xs font-mono">
      <div className="text-muted-foreground uppercase tracking-widest text-[10px] mb-1">{fmtTime(label)}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2">
          <span className="w-2 h-2" style={{ background: p.color }} />
          <span className="text-foreground">{p.dataKey}</span>
          <span className="ml-auto">{p.value}/min</span>
        </div>
      ))}
    </div>
  );
};

export const GateThroughputChart = ({ series = [] }) => {
  // pivot points to a unified array of { ts, gate1: v, gate2: v, ... }
  const data = useMemo(() => {
    if (!series.length) return [];
    const tsSet = new Map();
    for (const s of series) {
      for (const p of s.points) {
        const cur = tsSet.get(p.ts) || { ts: p.ts };
        cur[s.gate_name] = p.value;
        tsSet.set(p.ts, cur);
      }
    }
    return Array.from(tsSet.values()).sort((a, b) => new Date(a.ts) - new Date(b.ts));
  }, [series]);

  return (
    <div className="h-full flex flex-col min-h-0" data-testid="gate-throughput-chart">
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-border shrink-0">
        <span className="h-widget">Gate Throughput · people/min</span>
        <span className="font-mono text-[10px] text-muted-foreground">{series.length} gates · rolling {data.length} pts</span>
      </div>

      {/* Per-gate KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-1 px-2 pt-2 shrink-0">
        {series.map((s, i) => (
          <div key={s.gate_id} className="panel-elevated p-2" data-testid={`gate-kpi-${s.gate_id}`}>
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2" style={{ background: GATE_COLORS[i % GATE_COLORS.length] }} />
              <span className="font-display font-bold text-[11px] uppercase tracking-tight truncate">{s.gate_name}</span>
            </div>
            <div className="font-mono text-lg font-semibold tracking-tighter mt-1 leading-none">
              {s.latest}<span className="text-muted-foreground text-xs">/min</span>
            </div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5">
              avg {s.avg} · cap {s.capacity_per_min}
            </div>
          </div>
        ))}
      </div>

      <div className="flex-1 min-h-0 p-2">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
            <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" vertical={false} />
            <XAxis
              dataKey="ts"
              tick={{ fontSize: 10, fontFamily: "IBM Plex Mono", fill: "hsl(var(--muted-foreground))" }}
              tickFormatter={fmtTime}
              stroke="hsl(var(--border))"
              minTickGap={24}
            />
            <YAxis
              tick={{ fontSize: 10, fontFamily: "IBM Plex Mono", fill: "hsl(var(--muted-foreground))" }}
              stroke="hsl(var(--border))"
              width={32}
            />
            <Tooltip content={<TooltipBox />} />
            <Legend
              iconType="square"
              wrapperStyle={{ fontSize: 10, fontFamily: "IBM Plex Mono", letterSpacing: "1px", textTransform: "uppercase" }}
            />
            {series.map((s, i) => (
              <Line
                key={s.gate_id}
                type="monotone"
                dataKey={s.gate_name}
                stroke={GATE_COLORS[i % GATE_COLORS.length]}
                strokeWidth={1.5}
                dot={false}
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default GateThroughputChart;
